import { AbyssTheme as themeConfiguration } from '@src/app/browser';
import { useWizardContext } from '@src/features/ActionPaths/Wizard/context';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { FilterCriteriaWidget } from '@src/widgets/Criteria';
import { RemediationActionsWidget } from '@src/widgets/RiskRecords';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { isEmpty, isNil } from 'lodash';
import React, { useState } from 'react';

/**
 * ReviewConfirm
 *
 * Provides the user with a screen to verify the specified entrance criteria (filters) and actions to run on the
 * queried risk records.
 *
 * @param assets
 * @param form
 * @returns {React.JSX.Element|null}
 * @constructor
 */
export function ReviewConfirm({ assets = {}, form = {} }) {
  const [exitCount, setExitCount] = useState(null);

  const { actionPath, currentStep, mode } = useWizardContext();

  if (isNil(actionPath) || isEmpty(actionPath)) {
    return null;
  }

  const scopeCode = form.getValues('remediation.actionPathScopeCode');
  const remediationType = form.getValues('remediation.remediationMethod');
  const initiativeType = form.getValues('remediation.initiativeType');
  const assignments = form.getValues('remediation.assignments');

  const criteria = form?.getValues('criteria');
  const exitCriteriaMethod = form?.getValues('exitCriteriaMethod');

  return (
    <ErrorHandler location="src/features/ActionPaths/Wizard/components/steps/ReviewConfirm/ReviewConfirm.jsx">
      <Grid>
        <Grid.Col
          css={{ paddingTop: themeConfiguration?.theme?.space?.lg }}
          span={{
            xs: '100%',
          }}
        >
          <Heading offset={1}>{currentStep?.label}</Heading>
          <p>{currentStep?.description}</p>
        </Grid.Col>
        {exitCount == 0 && (
          <Grid.Col
            span={{
              xs: '100%',
            }}
          >
            <Alert title="Warning - Consider Refining Entrance Criteria" variant="warning">
              The Entrance and Exit Criteria contain records entering the action path AND being immediately remediated.
            </Alert>
          </Grid.Col>
        )}
        {mode !== 'manual' && [
          <Grid.Col
            key="entranceCriteria"
            span={{
              xs: '50%',
            }}
          >
            <FilterCriteriaWidget
              {...{
                context: 'entrance',
                criteria,
                filterKey: 'entranceCriteria',
              }}
            />
          </Grid.Col>,
          <Grid.Col
            key="exitCriteria"
            span={{
              xs: '50%',
            }}
          >
            <FilterCriteriaWidget
              {...{
                context: 'exit',
                criteria,
                criteriaMethod: exitCriteriaMethod,
                filterKey: 'exitCriteria',
                setCountCallback: setExitCount,
              }}
            />
          </Grid.Col>,
        ]}
        {mode === 'manual' && (
          <Grid.Col
            key="exitCriteria"
            span={{
              xs: '50%',
            }}
          >
            <FilterCriteriaWidget
              {...{
                context: 'exit',
                criteria,
                criteriaMethod: exitCriteriaMethod,
                filterKey: 'exitCriteria',
                setCountCallback: setExitCount,
              }}
            />
          </Grid.Col>
        )}
        <Grid.Col
          span={{
            xs: '100%',
          }}
        >
          <RemediationActionsWidget
            {...{
              assignments: assignments.map((assignment) => {
                const theAssignment = { ...assignment };
                const theAction = assets?.ListActions?.data?.content?.find((action) => {
                  return action?.id === theAssignment?.actionId;
                });

                theAssignment.actionName = theAction?.name;
                return theAssignment;
              }),
              initiativeType,
              remediationType,
              scopeCode,
            }}
          />
        </Grid.Col>
      </Grid>
    </ErrorHandler>
  );
}
